import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import type { SpmApiFilter } from '@/components/table-rest-api/filters';
import { showToastError, showToastSuccess } from '@/helpers';

interface CategoryData {
  id_category?: string | number;
  category_name: string;
}

interface ShopIntegrationData {
  shop_integration_status: string;
}

interface PartnerData {
  partner_name: string;
}

export interface ConfigField {
  key: string;
  label: string | Record<string, string>;
  type: 'text' | 'password' | 'email' | 'url' | 'number' | 'select' | 'multiselect' | 'checkbox' | 'textarea';
  required: boolean;
  placeholder?: string | Record<string, string>;
  helpText?: string | Record<string, string>;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
    step?: number;
    rows?: number;
  };
  options?: Array<{ value: string; label: string | Record<string, string> }>;
  remote?: {
    resource: string;
    label_field: string;
    value_field: string;
    description_field?: string;
  };
  /**
   * Field ownership. `merchant` (default, implicit) = filled by the merchant in
   * this config UI. `integrator` = set by the integrator via the API per shop —
   * HIDDEN from the merchant, resolved at render as `{integration.<key>}` with
   * `default` as the fallback until the integrator sets it.
   */
  owner?: 'merchant' | 'integrator';
  default?: string | number | boolean;
}

export interface ConfigStep {
  key: string;
  label: string | Record<string, string>;
  description?: string | Record<string, string>;
  icon?: string;
  fields: ConfigField[];
  depends_on?: string;
  on_complete?: {
    action: string;
    success_message: string | Record<string, string>;
    error_message: string | Record<string, string>;
  };
}

export interface IntegrationConfig {
  fields?: ConfigField[];
  steps?: ConfigStep[];
}

export interface Integration {
  id_integration: string;
  id_partner: string;
  slug: string;
  name: string;
  description: string;
  short_description?: string;
  // Champs localisés additifs (backend C10, coordonné G14). Consommés via
  // `pickLocalizedText` quand présents ; sinon repli sur les champs string
  // legacy ci-dessus. Tolérant à leur absence (rétro-compatible).
  name_i18n?: Record<string, string> | null;
  short_description_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  icon_url?: string;
  documentation_url?: string;
  config?: IntegrationConfig;
  // T1 — backend-computed: true once configuration is finished (required fields
  // filled + connection test passed). Gates activation in the UI.
  configured?: boolean;
  integration_categories_data?: CategoryData[];
  shop_integrations_data?: ShopIntegrationData[];
  partners_data?: PartnerData[];
  webhooks?: Record<string, string | undefined>;
  webhook_secret?: string;
  status: string;
  // Portée par boutique : `restricted` = intégration réservée par ShopiMind à une
  // sélection de boutiques. Absente tant que l'API ne l'expose pas (repli : publique).
  visibility?: 'public' | 'restricted';
  version?: string;
  external_auth?: string;
  created_at: string;
  updated_at: string;
}

export function useIntegrations(idShop: number) {
  const { t } = useI18n();

  const activeIntegrations = ref<Integration[]>([]);
  const availableIntegrations = ref<Integration[]>([]);
  const loadingActive = ref(false);
  const loadingAvailable = ref(false);
  const activeTotalItems = ref(0);
  const availableTotalItems = ref(0);
  // Drapeaux d'erreur additifs (G8) : permettent à la vue d'afficher un état
  // d'erreur inline + bouton « réessayer » au lieu du seul toast fugace.
  const errorActive = ref(false);
  const errorAvailable = ref(false);

  // Fields to fetch from API (only what we display)
  const apiFields = [
    'id_integration',
    'partner_name',
    'slug',
    'name',
    'name_i18n',
    'config',
    'short_description',
    'short_description_i18n',
    'icon_url',
    'id_category',
    'category_name',
    'shop_integration_status',
    'status',
    'visibility',
    'version',
    'external_auth',
  ].join(',');

  /**
   * Get shop integration status from the first element of shop_integrations_data array
   */
  const getShopIntegrationStatus = (integration: Integration): string => integration.shop_integrations_data?.[0]?.shop_integration_status || '';

  /**
   * Update shop integration status in the local state
   */
  const updateShopIntegrationStatus = (integration: Integration, newStatus: string): void => {
    if (!integration.shop_integrations_data) {
      // eslint-disable-next-line no-param-reassign
      integration.shop_integrations_data = [];
    }
    if (integration.shop_integrations_data.length === 0) {
      integration.shop_integrations_data.push({ shop_integration_status: newStatus });
    } else {
      // eslint-disable-next-line no-param-reassign
      integration.shop_integrations_data[0].shop_integration_status = newStatus;
    }
  };

  /**
   * Load integrations (active or available)
   */
  const loadIntegrations = async (
    type: 'active' | 'available',
    searchQuery: string,
    categoryFilter: (string | number)[],
    currentPage: number,
    rowsPerPage: number,
  ) => {
    const isActive = type === 'active';
    const loading = isActive ? loadingActive : loadingAvailable;
    const totalItems = isActive ? activeTotalItems : availableTotalItems;
    const integrations = isActive ? activeIntegrations : availableIntegrations;
    const errorFlag = isActive ? errorActive : errorAvailable;

    loading.value = true;
    errorFlag.value = false;
    try {
      const filters: SpmApiFilter[] = isActive
        ? [
          {
            field: 'id_shop',
            operator: SpmFilterOperator.EQUALS,
            values: idShop,
          },
          {
            field: 'shop_integration_status',
            operator: SpmFilterOperator.NOT_IN,
            values: ['uninstalled'],
          },
        ]
        : [
          {
            // Marketplace surfaces both fully active integrations and the
            // ones flagged as `soon` (visible but not installable yet).
            field: 'status',
            operator: SpmFilterOperator.IN,
            values: ['active', 'soon'],
          },
        ];

      // Add category filter if selected (multiple categories)
      if (categoryFilter && categoryFilter.length > 0) {
        filters.push({
          field: 'id_category',
          operator: SpmFilterOperator.IN,
          values: categoryFilter,
        });
      }

      // Add search filter if present (search in name only to avoid AND condition)
      if (searchQuery) {
        const searchValue = `%${searchQuery}%`;
        filters.push({
          field: 'name',
          operator: SpmFilterOperator.LIKE,
          values: searchValue,
        });
      }

      const offset = (currentPage - 1) * rowsPerPage;
      const params: Record<string, unknown> = {
        limit: rowsPerPage,
        offset,
        fields: apiFields,
      };

      if (filters.length > 0) {
        params.filters = filters;
      }

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/integrations',
        {},
        '',
        undefined,
        true,
        params,
      );

      if (response?.data) {
        integrations.value = response.data;
        totalItems.value = response.meta?.pagination?.total_items || response.data.length;
      }
    } catch (error) {
      console.error(`Error loading ${type} integrations:`, error);
      errorFlag.value = true;
      await showToastError(t(`integrations.errors.load${type === 'active' ? 'Active' : 'Available'}`));
    } finally {
      loading.value = false;
    }
  };

  /**
   * Install an integration
   */
  const installIntegration = async (integration: Integration) => {
    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        `/integrations/${integration.slug}/install`,
        {},
        '',
        {
          return_url: window.location.href,
        },
        true,
      );

      if (response?.statusCode === 200 && response?.data) {
        const { data } = response;

        // Case 1: OAuth authorization required
        if (data.requires_oauth && data.redirect_url) {
          return {
            success: true,
            requiresOAuth: true,
            redirectUrl: data.redirect_url,
            idShopIntegration: data.id_shop_integration,
            status: data.status,
          };
        }

        // Case 2: Installation successful, activation required
        if (data.requires_activation) {
          // Update local state with new status
          updateShopIntegrationStatus(integration, data.status);
          await showToastSuccess(t('integrations.success.installedNeedsActivation', { name: integration.name }));
          return {
            success: true,
            requiresActivation: true,
            idShopIntegration: data.id_shop_integration,
            status: data.status,
          };
        }

        // Default success case
        await showToastSuccess(t('integrations.success.installed', { name: integration.name }));
        return {
          success: true,
          idShopIntegration: data.id_shop_integration,
          status: data.status,
        };
      }

      // 404 : l'intégration n'est plus proposée à cette boutique (retirée du
      // catalogue ou portée restreinte) alors que sa carte était encore affichée.
      if (response?.error?.statusCode === 404) {
        await showToastError(t('integrations.errors.unavailable'));
        return { success: false, notFound: true };
      }

      await showToastError(t('integrations.errors.install'));
      return { success: false };
    } catch (error) {
      console.error('Error installing integration:', error);
      await showToastError(t('integrations.errors.install'));
      return { success: false };
    }
  };

  /**
   * Uninstall an integration
   */
  const uninstallIntegration = async (integration: Integration) => {
    try {
      const response = await nestCall(
        'delete',
        NestApiType.CLIENT_API,
        `/integrations/${integration.slug}`,
        {},
        '',
        {},
        true,
      );

      if (response?.statusCode === 200) {
        await showToastSuccess(t('integrations.success.uninstalled', { name: integration.name }));
        return true;
      }
      await showToastError(t('integrations.errors.uninstall'));
      return false;
    } catch (error) {
      console.error('Error uninstalling integration:', error);
      await showToastError(t('integrations.errors.uninstall'));
      return false;
    }
  };

  /**
   * Toggle integration status (activate/deactivate)
   */
  const toggleIntegrationStatus = async (integration: Integration) => {
    const currentStatus = getShopIntegrationStatus(integration);
    const isCurrentlyActive = currentStatus === 'active';
    const endpoint = isCurrentlyActive ? 'deactivate' : 'activate';
    const newStatus = isCurrentlyActive ? 'inactive' : 'active';

    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        `/integrations/${integration.slug}/${endpoint}`,
        {},
        '',
        {},
        true,
      );

      if (response?.statusCode === 200) {
        // Update local state immediately for better UX
        updateShopIntegrationStatus(integration, newStatus);

        const messageKey = newStatus === 'active'
          ? 'integrations.success.activated'
          : 'integrations.success.deactivated';
        await showToastSuccess(t(messageKey, { name: integration.name }));
        return true;
      }
      await showToastError(t('integrations.errors.toggleStatus'));
      return false;
    } catch (error) {
      console.error('Error toggling integration status:', error);
      await showToastError(t('integrations.errors.toggleStatus'));
      return false;
    }
  };

  /**
   * Cancel installation (for pending_auth status)
   */
  const cancelInstallation = async (integration: Integration) => {
    try {
      const response = await nestCall(
        'delete',
        NestApiType.CLIENT_API,
        `/integrations/${integration.slug}`,
        {},
        '',
        {},
        true,
      );

      if (response?.statusCode === 200) {
        await showToastSuccess(t('integrations.success.cancelled', { name: integration.name }));
        return true;
      }
      await showToastError(t('integrations.errors.cancel'));
      return false;
    } catch (error) {
      console.error('Error cancelling installation:', error);
      await showToastError(t('integrations.errors.cancel'));
      return false;
    }
  };

  /**
   * Check if an integration is already installed
   */
  const isIntegrationInstalled = (integration: Integration): boolean => activeIntegrations.value.some(
    (activeInt) => activeInt.id_integration === integration.id_integration,
  );

  /**
   * Get integration details by slug
   */
  const getIntegrationDetails = async (slug: string): Promise<Integration | null> => {
    try {
      const filters: SpmApiFilter[] = [
        {
          field: 'slug',
          operator: SpmFilterOperator.EQUALS,
          values: slug,
        },
      ];

      const fields = [
        'id_integration',
        'partner_name',
        'slug',
        'name',
        'name_i18n',
        'short_description',
        'short_description_i18n',
        'description',
        'description_i18n',
        'icon_url',
        'documentation_url',
        'version',
        'visibility',
        'id_category',
        'category_name',
      ].join(',');

      const params: Record<string, unknown> = {
        limit: 1,
        offset: 0,
        fields,
        filters,
      };

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/integrations',
        {},
        '',
        undefined,
        true,
        params,
      );

      if (response?.data && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (error) {
      console.error('Error loading integration details:', error);
      await showToastError(t('integrations.errors.loadDetails'));
      return null;
    }
  };

  return {
    // State
    activeIntegrations,
    availableIntegrations,
    loadingActive,
    loadingAvailable,
    activeTotalItems,
    availableTotalItems,
    errorActive,
    errorAvailable,
    // Methods
    loadIntegrations,
    installIntegration,
    uninstallIntegration,
    toggleIntegrationStatus,
    cancelInstallation,
    isIntegrationInstalled,
    getShopIntegrationStatus,
    getIntegrationDetails,
  };
}
